import { ContextDataProperty, DynamicFormsHookType } from '../../../../enums';
import Component from 'formiojs/components/_classes/component/Component';
import { configureLinkBehavior, configureLinkTemplate } from '../../base/edit-link-helper';
import { isInPreviewMode } from '../../../../utils/formio-component-utils';
import NestedComponent from 'formiojs/components/_classes/nested/NestedComponent';
import FormComponent from 'formiojs/components/form/Form';
import { getContextDataValue } from '../../../../utils';
import BaseRepeaterWidget from '../../../../components/form-components/base/base-repeater-widget/base-repeater-widget';

enum ComponentState {
	ListView,
	EditView
}

export default class VueFormioListComponent extends BaseRepeaterWidget {
	static schema(...extend: any): any {
		return Component.schema({}, ...extend);
	}

	get isInPreviewMode() {
		return isInPreviewMode(this);
	}

	get isLender() {
		return getContextDataValue(this, ContextDataProperty.IsLender);
	}

	public vueComponent: any;
	protected state = ComponentState.ListView;
	protected vueContainerRef = 'vue-container';

	constructor(component: any, options: any, data: object) {
		super(component, options, data);

		const hookParams = { componentType: this.component.type, schema: this.component };
		const hookResponse = this.hook(DynamicFormsHookType.CreateVueComponent, hookParams);

		// Ensure the response is not null or the parameters passed in (for some reason, formio returns back the params if it can't find a matching hook)
		if (hookResponse != null && hookResponse !== hookParams) {
			this.vueComponent = hookResponse;
		}

		if (!this.builderMode && this.vueComponent) {
			// add-existing-entity is used to add an existing entity to the list - we have to load the details and avoid popping the edit screen
			this.vueComponent.$on('add-existing-entry', (data: any) => this.addEntry(data));
			this.vueComponent.$on('add-entry', () => this.addEntry());
			this.vueComponent.$on('edit-entry', (id: number) => this.editRow(id));
			this.vueComponent.$on('remove-entry', (id: number) => this.removeRow(id));
		}
	}

	async addEntry(data: any | null = null) {
		var addingExistingEntry = data !== null;
		var entryData = addingExistingEntry ? await this.buildExistingEntryResult(data) : {};

		this.addRow();
		const rowIndex = this.editRows.length - 1;
		if (this.isLender) {
			await this.saveOrEditRow(rowIndex, entryData, addingExistingEntry);
		} else {
			setTimeout(async () => {
				await this.saveOrEditRow(rowIndex, entryData, addingExistingEntry);
			}, 1000);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected async buildExistingEntryResult(data: any) {
		throw new Error('Not Implemented');
	}
	protected async saveOrEditRow(rowIndex: number, data: any, addingExistingEntry: boolean) {
		const formComponent: FormComponent = this.editRows[rowIndex].components[0];
		await formComponent.subFormReady;
		formComponent.setValue(
			{
				...formComponent.subForm.getValue(),
				data: data
			},
			{ fromSubmission: true }
		);

		this.editRows[rowIndex].data = formComponent.data;
		await this.saveNewRow(rowIndex, addingExistingEntry);
	}

	async saveNewRow(rowIndex: number, addingExistingEntry: boolean = false) {
		this.saveRow(rowIndex);

		// Partial save triggers a data- re-fetch that is needed to show any existing related-entities
		const savePromise = this.partialSave(rowIndex).finally(() => {
			// If this is not an existing entity, must pop up the edit screen
			if (!addingExistingEntry) {
				return this.editRow(rowIndex);
			}
		});

		await savePromise;
	}

	async attach(element: HTMLElement) {
		// we did this to by pass the logic ran in EditGrid since it creates event listeners that we no longer need
		await NestedComponent.prototype.attach.call(this, element);

		if (this.builderMode) {
			configureLinkBehavior(this, element);
			return;
		} else if (isInPreviewMode(this)) {
			return;
		}

		this.loadRefs(element, {
			[this.rowRef]: 'multiple',
			[this.vueContainerRef]: 'single'
		});

		if (this.state === ComponentState.EditView) {
			for (let rowIndex = 0; rowIndex < this.rowElements.length; rowIndex++) {
				const row = this.rowElements[rowIndex];
				const editRow = this.editRows[rowIndex];

				if (this.isOpen(editRow) && this.state === ComponentState.EditView) {
					await this.attachComponents(row, editRow.components);
				}
			}
		} else if (this.vueComponent && this.state === ComponentState.ListView) {
			await this.preAttachVueComponentSetup();
			((this.refs[this.vueContainerRef] as unknown) as HTMLDivElement).appendChild(this.vueComponent.$el);
		}
	}
	public async beforeSubmit() {
		// this is needed because when we 'save and return' we want to change the element that is being rendered.
		// Setting the state in the save function is not enough since we save when we create a new entity as well.
		this.state = ComponentState.ListView;
		return super.beforeSubmit();
	}

	render(children: any, _topLevel?: boolean): any {
		if (this.builderMode) {
			return configureLinkTemplate(this);
		}

		return super.render(
			children ||
				this.renderTemplate('vueDataList', {
					ref: {
						row: this.rowRef,
						vueContainer: this.vueContainerRef
					},
					state: this.state,
					rows: this.editRows.map(this.renderRow.bind(this)),
					openRows: this.editRows.map(row => this.isOpen(row) && this.state === ComponentState.EditView),
					errors: this.editRows.map(row => row.error)
				})
		);
	}

	checkComponentValidity(data: any, dirty: boolean, row: any, options: any = {}): boolean | Promise<boolean> {
		const superResult = super.checkComponentValidity(data, dirty, row, options);
		const { async = false, silentCheck = false } = options;

		if (async) {
			return (superResult as Promise<boolean>).then(valid => {
				const isVueComponentValid = this.validateVueComponent(dirty, silentCheck);

				return valid && isVueComponentValid;
			});
		}

		return superResult && this.validateVueComponent(dirty, silentCheck);
	}

	addMessages(_messages: any) {
		// Overwriting existing implementation and leaving empty since we don't want formio to show the validation errors b/c they are being
		// 	controlled by the vue component
	}

	protected validateVueComponent(dirty: boolean, silentCheck: boolean): boolean {
		// Run validation within the vue component to get the validation messages
		this.vueComponent?.validate?.();
		const validationMessages = this.vueComponent?.validationMessages ?? [];

		// Set the validation messages so formio can pick them up
		this.setComponentValidity(validationMessages, dirty, silentCheck);

		return validationMessages.length === 0;
	}

	// Override - We do not care about the loading spinner logic
	protected async partialSaveRow(rowIndex: number) {
		await this.partialSave(rowIndex);
	}

	saveRow(rowIndex: number, modified?: boolean) {
		this.state = ComponentState.ListView;

		const result = super.saveRow(rowIndex, modified);
		if (result) {
			this.emitEditEndEvent();
		}

		return result;
	}

	async removeRow(rowIndex: number) {
		this.state = ComponentState.ListView;

		const result: any = super.removeRow(rowIndex);
		this.emitEditEndEvent();

		return result;
	}

	async editRow(rowIndex: number) {
		this.state = ComponentState.EditView;
		const result: any = super.editRow(rowIndex);
		this.emitEditEvent();

		return result;
	}

	cancelRow(rowIndex: number) {
		const editRow = this.editRows[rowIndex];
		const originalState = editRow.state;

		const result = super.cancelRow(rowIndex);

		if (originalState !== editRow.state) {
			this.emitEditEndEvent();
		}

		return result;
	}

	protected async preAttachVueComponentSetup() {
		// this method has been intentionally been left blank so we can access functionality on primary-role-repeater-widget-vue
	}
}
